import type React from 'react';

import { Text } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { Translate } from '@change-corgi/core/react/i18n';
import { useKnownAvailableParams } from '@change-corgi/core/react/router';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { ModuleSeparator, PetitionActivityLink } from 'src/app/pages/starterDashboardV2/shared/components';

import { PetitionStats } from './components/PetitionStats';
import { PetitionStrengthScore } from './components/PetitionStrengthScore';
import { ReachedYourGoal } from './components/ReachedYourGoal';
import { usePetitionOverview } from './hooks/usePetitionOverview';

export const PetitionOverview: React.FC = () => {
	const {
		data: { strengthTipsEnabled },
	} = usePetitionOverview();
	const { slug } = useKnownAvailableParams<{ slug: string }>();

	const {
		data: {
			viewer: {
				experiments: {
					starterExperiencePetitionActivityV1: { variation: activityPageVariation },
				},
			},
			petition: {
				activityNewCounts: { totalNewCountDisplay },
			},
		},
	} = useStarterDashboardV2PageData();

	return (
		<section
			className="bg-white px-4 py-8 md:p-12 md:pt-4 lg:pt-12"
			aria-labelledby="petition-overview-title"
			role="group"
		>
			<div className="flex items-center justify-between md:mb-6 lg:pb-2">
				<Text
					className="text-size-heading-small"
					size="small"
					as="h3"
					id="petition-overview-title"
					data-qa="petition-overview-title"
					data-testid="petition-overview-title"
				>
					<Translate value="corgi.petition.starter_dashboard.petition_overview.title" />
				</Text>

				{activityPageVariation === 'enabled' && <PetitionActivityLink slug={slug} badgeCount={totalNewCountDisplay} />}
			</div>
			<div
				className={cn(
					'grid grid-cols-[1fr] grid-rows-[auto_1fr] gap-y-2 border-[unset] pt-4 md:gap-x-5 md:gap-y-0 md:pt-0',
					{
						'md:grid-cols-[minmax(0,1fr)_auto_minmax(auto,220px)] lg:grid-cols-[minmax(0,1fr)_auto_auto]':
							strengthTipsEnabled,
					},
				)}
			>
				<div className="relative">
					<PetitionStats />
				</div>
				<ModuleSeparator orientation="vertical" className="border-stroke-weaker! hidden md:block" />
				<div className="flex w-full flex-col items-center justify-center p-0 md:min-w-[220px] md:items-start lg:min-w-3xs lg:pr-0 lg:pl-4">
					{strengthTipsEnabled && <PetitionStrengthScore />}
					<ModuleSeparator orientation="horizontal" className="hidden md:my-6 md:block" />
					<ReachedYourGoal />
				</div>
			</div>
		</section>
	);
};
